import { InterventionModalData } from '../intervention-modal/intervention-modal';
import { formaterDateLongueAvecJour, formaterHorairesIntervention } from '../../Services/format';
import { InterventionBrute, libelleTypeIntervention } from '../../Services/interventions';

/** Modèle commun au calendrier de maintenance et aux cartes des prochaines interventions. */
export type MaintenanceIntervention = {
  id: number;
  type: InterventionBrute['type'];
  nature?: InterventionBrute['nature'];
  title: string;
  site: string;
  comment: string;
  installationName?: string;
  startsAt: string;
  createdAt?: string | null;
  dateDeb?: string | null;
  endsAt?: string;
  color: string;
  allDay?: boolean;
  resolved?: boolean | null;
  ticketId?: number;
  installationId?: number;
  ticketNumero?: string;
  ticketDateCreation?: string | null;
  ticketLibelle?: string | null;
};

export const couleursInterventions = {
  intervention_preventive: '#9ed7b0',
  intervention_curative: '#f2aaaa',
};
export function formatTimeRange(item: MaintenanceIntervention): string {
  return formaterHorairesIntervention(item.dateDeb, item.endsAt);
}

export function trackIntervention(_: number, item: MaintenanceIntervention) {
  return `${item.type}-${item.id}`;
}

export function installationLabel(item: MaintenanceIntervention): string {
  return (
    item.installationName ||
    (item.ticketNumero ? `Ticket ${item.ticketNumero}` : 'Intervention du parc')
  );
}

/** La création reste distincte de la date prévue/réalisée, même si l’API ne la renseigne pas. */
export function mapperIntervention(item: InterventionBrute): MaintenanceIntervention {
  const startsAt = item.date_deb ?? item.date_intervention;
  const endsAt = item.date_fin ?? undefined;
  const commentaire = item.commentaire?.trim() || '';
  const intervenant = item.intervenant?.trim() || 'Equipe maintenance';
  const installationName = item.ticket?.installation?.nom?.trim();

  return {
    id: item.id,
    type: item.type,
    nature: item.nature,
    title: commentaire,
    site: intervenant,
    comment: commentaire,
    installationName,
    startsAt,
    createdAt: item.dateCreation,
    dateDeb: item.date_deb,
    endsAt,
    color: couleursInterventions[item.type],
    allDay: estTouteLaJournee(startsAt, endsAt),
    resolved: item.pb_resolu ?? null,
    ticketId: item.ticket?.id,
    installationId: item.ticket?.installation?.id,
    ticketNumero: item.ticket?.numero,
    ticketDateCreation: item.ticket?.date_ouverture,
    ticketLibelle: item.ticket?.libelle,
  };
}

export function comparerInterventionsChronologiquement(
  a: MaintenanceIntervention,
  b: MaintenanceIntervention,
): number {
  const dateA = new Date(a.startsAt).getTime();
  const dateB = new Date(b.startsAt).getTime();

  if (Number.isFinite(dateA) && Number.isFinite(dateB)) {
    return dateA - dateB;
  }

  return a.startsAt.localeCompare(b.startsAt);
}

export function estTouteLaJournee(startsAt: string, endsAt?: string): boolean {
  if (!endsAt) return false;
  const debut = new Date(startsAt).getTime();
  const fin = new Date(endsAt).getTime();
  if (!Number.isFinite(debut) || !Number.isFinite(fin) || fin <= debut) return false;
  return fin - debut >= 8 * 60 * 60 * 1000;
}

export function lienTicket(item: MaintenanceIntervention): string[] | null {
  if (!item.ticketId) return null;
  return ['/ticket', String(item.ticketId)];
}

export function creerModalIntervention(
  intervention: MaintenanceIntervention,
): InterventionModalData {
  return {
    nature: intervention.nature,
    eyebrow: `Intervention ${libelleTypeIntervention(intervention.type).toLocaleLowerCase('fr-FR')}`,
    title: installationLabel(intervention),
    informations: [
      { label: 'Date', value: formaterDateLongueAvecJour(intervention.startsAt), icon: 'event' },
      { label: 'Horaire', value: formatTimeRange(intervention), icon: 'schedule' },
      { label: 'Intervenant', value: intervention.site, icon: 'badge' },
      {
        label: 'Statut',
        value:
          intervention.nature === 'planification'
            ? 'Planifiée'
            : intervention.resolved === true
              ? 'Résolu'
              : intervention.resolved === false
                ? 'Non résolu'
                : 'Non renseigné',
        icon: 'task_alt',
      },
    ],
    detailsLabel: 'Commentaire',
    details: intervention.comment || 'Aucun commentaire disponible.',
    referencePhotos: {
      id: intervention.id,
      categorie: intervention.type === 'intervention_preventive' ? 'preventive' : 'curative',
    },
    ticketLink: lienTicket(intervention),
    ticketLabel: intervention.ticketId
      ? `Ouvrir le ticket ${intervention.ticketNumero ?? `#${intervention.ticketId}`}`
      : undefined,
  };
}
